import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from '../../store';
import AdminUsers from './Users';
import AdminHealth from './Health';
import AdminAgent from './Agent';
import AdminSkills from './AdminSkills';
import AdminTokenUsage from './AdminTokenUsage';
import ChypsIntake from '../chyps/ChypsIntake';

const IS_DEV = process.env.REACT_APP_DEPLOY_MODE === 'DEV';

export type AdminTabId = 'users' | 'health' | 'agent' | 'chyps' | 'skills' | 'token-usage' | 'dev-tools';

const BASE_TABS: { id: AdminTabId; label: string; desc: string }[] = [
  { id: 'users',       label: 'Users',           desc: 'Manage user accounts and tiers' },
  { id: 'health',      label: 'Health',           desc: 'Backend health and API metrics' },
  { id: 'agent',       label: 'Agent',            desc: 'Ask Dr. Schmoe and rehab plans' },
  { id: 'chyps',       label: 'Symptoms Profile', desc: 'Symptoms questionnaire and secure patient links' },
  { id: 'skills',      label: 'MCP Agent Center', desc: 'Portal skills for the DrSchmoe Agent' },
  { id: 'token-usage', label: 'Token Usage',      desc: 'AI credit balances and usage by group / user' },
];

const DEV_TAB = { id: 'dev-tools' as AdminTabId, label: '⚙ Dev Tools', desc: 'Local dev utilities' };

const TABS = IS_DEV ? [...BASE_TABS, DEV_TAB] : BASE_TABS;

// ── Styles ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  padding: 0;
  max-width: 100%;
`;

const Header = styled.div`
  padding: 24px 24px 0;
  margin-bottom: 0;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin: 0 0 16px 0;
  font-size: 14px;
`;

const TabList = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 24px 0 0;
  margin-bottom: 0;
`;

const Tab = styled.button<{ $active: boolean; $dev?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: ${p => p.$active ? 600 : 500};
  color: ${p => p.$active ? (p.$dev ? '#7c3aed' : '#111827') : (p.$dev ? '#8b5cf6' : '#6b7280')};
  background: none;
  border: none;
  border-bottom: 3px solid ${p => p.$active ? (p.$dev ? '#7c3aed' : '#3b82f6') : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: ${p => p.$dev ? '#7c3aed' : '#111827'}; }
`;

const TabPanel = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 400px;
`;

// ── Dev Tools panel ───────────────────────────────────────────────────────────
const DevGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const DevCard = styled.a`
  display: block;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 12px;
  padding: 20px 22px;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: #7c3aed;
    box-shadow: 0 2px 12px rgba(124, 58, 237, 0.08);
  }
`;

const DevCardIcon = styled.div`
  font-size: 28px;
  margin-bottom: 10px;
`;

const DevCardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e1b4b;
  margin-bottom: 4px;
`;

const DevCardDesc = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 12px;
`;

const DevCardUrl = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;
  font-family: 'Menlo', monospace;
`;

const DevBadge = styled.div`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #7c3aed;
  background: #ede9fe;
  border-radius: 4px;
  padding: 2px 7px;
  margin-bottom: 16px;
`;

const DEV_TOOLS = [
  {
    icon: '🎬',
    title: 'Video Demo Library',
    desc: 'All demo details, assets, clip timing, ElevenLabs TTS config, redaction commands, and marketing copy with copy buttons.',
    url: '/video-demo-library',
  },
  {
    icon: '🎭',
    title: 'Video Redactor',
    desc: 'Frame-by-frame video editor to draw ROI boxes over sensitive patient info. Exports annotations JSON for the add_blur.py script.',
    url: '/video-redactor',
  },
  {
    icon: '🖼',
    title: 'Logo Preview',
    desc: 'Preview all logo variants and brand assets.',
    url: '/logo-preview',
  },
];

function DevToolsPanel() {
  return (
    <div>
      <DevBadge>localhost only</DevBadge>
      <DevGrid>
        {DEV_TOOLS.map(tool => (
          <DevCard key={tool.url} href={tool.url} target="_blank" rel="noopener noreferrer">
            <DevCardIcon>{tool.icon}</DevCardIcon>
            <DevCardTitle>{tool.title}</DevCardTitle>
            <DevCardDesc>{tool.desc}</DevCardDesc>
            <DevCardUrl>{tool.url}</DevCardUrl>
          </DevCard>
        ))}
      </DevGrid>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const AdminControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAllowedAdmin = useSelector((s: RootState) => s.user.isAllowedAdmin);
  const profile = useSelector((s: RootState) => s.user.profile);

  const tabParam = searchParams.get('tab') as AdminTabId | null;
  const activeTab: AdminTabId =
    TABS.some(t => t.id === tabParam) ? tabParam! : 'users';

  const setTab = (id: AdminTabId) => setSearchParams({ tab: id }, { replace: true });

  useEffect(() => {
    if (!profile) { navigate('/login'); return; }
    if (isAllowedAdmin !== true) { navigate('/'); return; }
  }, [profile, isAllowedAdmin, navigate]);

  if (!profile || isAllowedAdmin !== true) return null;

  return (
    <Container>
      <Header>
        <Title>Admin Control Center</Title>
        <Subtitle>Super admin tools and system management.</Subtitle>
        <TabList role="tablist">
          {TABS.map(t => (
            <Tab
              key={t.id}
              role="tab"
              aria-selected={activeTab === t.id}
              $active={activeTab === t.id}
              $dev={t.id === 'dev-tools'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </Tab>
          ))}
        </TabList>
      </Header>
      <TabPanel role="tabpanel">
        {activeTab === 'users'     && <AdminUsers embedded />}
        {activeTab === 'health'    && <AdminHealth embedded />}
        {activeTab === 'agent'     && <AdminAgent embedded />}
        {activeTab === 'chyps'     && <ChypsIntake embedded />}
        {activeTab === 'skills'      && <AdminSkills embedded />}
        {activeTab === 'token-usage' && <AdminTokenUsage />}
        {activeTab === 'dev-tools'   && IS_DEV && <DevToolsPanel />}
      </TabPanel>
    </Container>
  );
};

export default AdminControlCenter;
